import { useEffect, useState } from 'react';
import {
  resolveTheme,
  useThemeStore,
  type ResolvedTheme,
} from '@/stores/theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Single owner of the live theme. Resolves the persisted preference (consulting
 * the OS when `system`), writes it to `<html data-theme>` + `color-scheme`, and
 * re-runs when either the preference or the OS setting changes.
 *
 * Mount exactly once near the app root. Returns the resolved `'light' | 'dark'`
 * so chrome that can't read CSS tokens (e.g. the sonner `<Toaster>`) can match.
 *
 * The initial `data-theme` is already set pre-paint by the bootstrap script in
 * `index.html`; this hook keeps it in sync for the lifetime of the session.
 */
export function useApplyTheme(): ResolvedTheme {
  const pref = useThemeStore((s) => s.pref);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(pref));

  useEffect(() => {
    const apply = () => {
      const next = resolveTheme(pref);
      setResolved(next);
      document.documentElement.dataset.theme = next;
      document.documentElement.style.colorScheme = next;
    };

    apply();

    // Only `system` needs to react to OS changes.
    if (pref !== 'system') return;

    const mql = window.matchMedia(DARK_QUERY);
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [pref]);

  return resolved;
}
